import React from 'react';

import { Root } from '@atlaskit/navigation-system/layout/root';
import { usePanelContext } from '@atlassian/entry-point-panel/use-panel-context';
import { defineMessage, useIntl } from 'react-intl';

import { GoalCapabilitiesProvider } from '@townsquare/goal-capabilities';
import { GriffinTopNavigation } from '@townsquare/griffin-navigation';
import { GlobalAppShortcutsProvider } from '@townsquare/griffin-navigation/global-navigation-context';
import type { RenderLegacyTopNavContent } from '@townsquare/griffin-navigation/types';
import { NavPlatformSideNav } from '@townsquare/nav-platform-integration/NavPlatformSideNav';
import { TOWNSQUARE_APP_SWITCHER_START_SLOTS } from '@townsquare/nav-platform-integration/app-switcher-slot';
import {
	TownsquareGlobalRoot,
	useTownsquareLegacyTopNavRenderer,
} from '@townsquare/nav-platform-integration/global-root';
import { AppHomeWrapper } from '@townsquare/navigation/app-home-wrapper';
import { IntroPopup } from '@townsquare/onboarding/intro-popup';
import { ProjectCapabilitiesProvider } from '@townsquare/project-capabilities';
import { SIDEBAR_PANEL } from '@townsquare/sidebar-panel/panels';
import { usePreloadedEntryPointPanel } from '@townsquare/sidebar-panel-utils/hooks/use-preloaded-entry-point-panel';
import { SiteBannerProvider } from '@townsquare/site-banner';
import { fg } from '@townsquare/stat-sig/gate';
import { useNoWorkspaceExperience } from '@townsquare/workspace-store';

import { ProductHome } from '../griffin-navigation/ProductHome';
import { useSideNavAppItemsForNavPlatform } from '../griffin-navigation/use-side-nav-app-items';
import { useSideNavItems } from '../griffin-navigation/use-side-nav-items';
import { useUniversalCreateItems } from '../griffin-navigation/use-universal-create-items';
import { GriffinLayout } from '../layouts/Home/GriffinLayout';

const GOALS_APP = 'goals';
// Goals nav-customisation is being disabled until it has it's own siteID/activationId
const GOALS_CUSTOMIZATION_STORE = 'local';

export function GriffinRoute(): React.JSX.Element {
	return fg('navx-global-top-nav-goals') ? <GriffinGlobalRoute /> : <GriffinLegacyRoute />;
}

export function GriffinLegacyRoute(): React.JSX.Element {
	return (
		<Root isSideNavShortcutEnabled>
			<SiteBannerProvider postOfficeBanners={[]}>
				<GlobalAppShortcutsProvider>
					<GriffinRouteComponent />
				</GlobalAppShortcutsProvider>
			</SiteBannerProvider>
		</Root>
	);
}

export function GriffinGlobalRoute(): React.JSX.Element {
	const { formatMessage } = useIntl();
	const noWorkspaceExperience = useNoWorkspaceExperience();
	const preloadedPanel = usePreloadedEntryPointPanel();
	const { Panel: SidebarPanel, Provider: SidebarPanelContext } = usePanelContext(
		SIDEBAR_PANEL,
		preloadedPanel,
	);

	return (
		<SidebarPanelContext>
			<GlobalAppShortcutsProvider>
				<TownsquareGlobalRoot
					app={GOALS_APP}
					appLabel={formatMessage(goalsAppLabelMessage)}
					customizationStore={GOALS_CUSTOMIZATION_STORE}
					TopNav={GoalsGlobalTopNav}
					sideNav={
						noWorkspaceExperience ? null : (
							<GoalCapabilitiesProvider>
								<ProjectCapabilitiesProvider>
									<PlatformSideNav />
								</ProjectCapabilitiesProvider>
							</GoalCapabilitiesProvider>
						)
					}
				>
					<SiteBannerProvider postOfficeBanners={[]}>
						<GriffinLayout PlatformSidebarPanel={SidebarPanel} />
						{!fg('townsquare_deprecate_hisi_onboarding') && noWorkspaceExperience && <IntroPopup />}
					</SiteBannerProvider>
				</TownsquareGlobalRoot>
			</GlobalAppShortcutsProvider>
		</SidebarPanelContext>
	);
}

function GoalsGlobalTopNav() {
	const renderLegacyTopNavContent = useTownsquareLegacyTopNavRenderer(
		TOWNSQUARE_APP_SWITCHER_START_SLOTS,
	);

	return (
		<GoalCapabilitiesProvider>
			<ProjectCapabilitiesProvider>
				<GriffinTopSection renderLegacyTopNavContent={renderLegacyTopNavContent} />
			</ProjectCapabilitiesProvider>
		</GoalCapabilitiesProvider>
	);
}

const GriffinTopSection = ({
	renderLegacyTopNavContent,
}: {
	/** Only passed by the gated GlobalRoot top nav. */
	renderLegacyTopNavContent?: RenderLegacyTopNavContent;
}) => {
	const noWorkspaceExperience = useNoWorkspaceExperience();
	const { items: universalCreateItems, compact } = useUniversalCreateItems();

	return (
		<GriffinTopNavigation
			renderProductHome={() => (
				<AppHomeWrapper>
					<ProductHome />
				</AppHomeWrapper>
			)}
			universalCreateItems={universalCreateItems}
			compact={compact}
			hideSideNavToggleButton={noWorkspaceExperience}
			UNSAFE__renderLegacyTopNavContent={renderLegacyTopNavContent}
		/>
	);
};

function GriffinRouteComponent() {
	const noWorkspaceExperience = useNoWorkspaceExperience();

	const hideSideNav = noWorkspaceExperience;

	const preloadedPanel = usePreloadedEntryPointPanel();
	const { Panel: SidebarPanel, Provider: SidebarPanelContext } = usePanelContext(
		SIDEBAR_PANEL,
		preloadedPanel,
	);

	return (
		<>
			<SidebarPanelContext>
				<GoalCapabilitiesProvider>
					<ProjectCapabilitiesProvider>
						<GriffinTopSection />
						{!hideSideNav && <PlatformSideNav />}
					</ProjectCapabilitiesProvider>
				</GoalCapabilitiesProvider>
				<GriffinLayout PlatformSidebarPanel={SidebarPanel} />
			</SidebarPanelContext>
			{!fg('townsquare_deprecate_hisi_onboarding') && noWorkspaceExperience && <IntroPopup />}
		</>
	);
}

function PlatformSideNav() {
	const sidebarItems = useSideNavItems();
	const appShortcuts = useSideNavAppItemsForNavPlatform();
	const { formatMessage } = useIntl();

	const disableMenuCustomization = true;

	return (
		<NavPlatformSideNav
			app={GOALS_APP}
			appLabel={formatMessage(goalsAppLabelMessage)}
			appItems={sidebarItems}
			appShortcuts={appShortcuts}
			customizationStore={GOALS_CUSTOMIZATION_STORE}
			disableMenuCustomization={disableMenuCustomization}
		/>
	);
}

const goalsAppLabelMessage = defineMessage({
	id: 'townsquare.goals.app.label',
	defaultMessage: 'Goals',
	description: 'Label for the app when shown in the global side navigation.',
});
